import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';

/**
* This builds upon BasicDropdown, adding our styles and common patterns
* Dropdown will close automatically after clicking within the content
* Each item in the `content` block will need to include the first passed argument in their classes

* Example usage:
<Ui::Classic::DropdownMenu @left="-104" @top="17">
  <:trigger>
    <a class="ml-2 btn btn--brand btn--arrow">
      Add employee
    </a>
  </:trigger>
  <:content as |itemClass|>
    <LinkTo
      @route="employees.directory.add-employee.from-location"
      class="{{itemClass}} align-middle icon-building"
    >
      Add from another location
    </LinkTo>
    <LinkTo
      @route="employees.directory.add-employee.new"
      class="{{itemClass}} align-middle icon-add"
    >
      Create new
    </LinkTo>
  </:content>
</Ui::Classic::DropdownMenu>

* There are several likely ways we can expand the usefulness of this component:
* - Find more similarities between the dropdown items to consolidate
* - Expand to cover when ember-power-select-option classes are used (styling is likely quite similar, but it's not copy/paste like the others)
*/

interface DropdownMenuSignature {
  Args: {
    disabled?: boolean;
    onOpen?: () => void;
    renderInPlace?: boolean;

    left?: string;
    right?: string;
    top: string;
    width?: string;
  };
  Blocks: {
    content: [itemClass: string];
    trigger: [];
  };
  Element: HTMLDivElement;
}

export default class DropdownMenu extends Component<DropdownMenuSignature> {
  get style(): string {
    const { left, right, top, width } = this.args;
    let style = `left: ${left}px; right: ${right}px; margin-top: ${top}px;`;
    if (width) {
      style += ` width: ${width};`;
    }
    return style;
  }

  @action
  close(close: () => void): void {
    close();
  }

  <template>
    <BasicDropdown @onOpen={{@onOpen}} @renderInPlace={{@renderInPlace}} @disabled={{@disabled}} as |dropdown|>
      <dropdown.Trigger>
        <div class="dropdown-trigger {{if @disabled 'cursor-default'}}" ...attributes>
          {{yield to="trigger"}}
        </div>
      </dropdown.Trigger>
      <dropdown.Content>
        <div
          class="dropdown-menu rounded-md"
          style={{this.style}}
          {{on "click" (fn this.close dropdown.actions.close)}}
        >
          {{yield "dropdown-item px-4" to="content"}}
        </div>
      </dropdown.Content>
    </BasicDropdown>
  </template>
}
